import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { get } from "../api/client";
import { useLoad } from "../api/hooks";
import type { UserProfile, Video } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { HandleSheet, initialsOf } from "../components/HandleSheet";
import { Shell } from "../components/Shell";
import { Dot, ErrorLine, VidFrame, useToast } from "../components/ui";
import { fmtDate, fmtDuration } from "../lib/format";
import { videoStatus } from "../lib/status";

export default function UserPage() {
  const { handle: rawHandle = "" } = useParams<{ handle: string }>();
  const handle = rawHandle.replace(/^@+/, "").toLowerCase();
  const [params, setParams] = useSearchParams();
  const teamFilter = params.get("team") || "";
  const { me, plan } = useAuth();
  const nav = useNavigate();
  const [toast, say] = useToast();
  const [editHandle, setEditHandle] = useState(false);

  const q = useLoad<UserProfile>(
    () => get<UserProfile>(`/v1/users/${encodeURIComponent(handle)}${teamFilter ? `?team=${encodeURIComponent(teamFilter)}` : ""}`),
    [handle, teamFilter],
  );

  const data = q.data;
  const isSelf = data?.is_self ?? (me?.user.handle === handle);
  const canCreate = me?.can_create !== false;
  const managedTeams = (me?.teams ?? []).filter((t) => t.role === "manager");

  const setTeam = (tid: string) => {
    const next = new URLSearchParams(params);
    if (tid) next.set("team", tid);
    else next.delete("team");
    setParams(next);
  };

  return (
    <Shell
      brand
      active={isSelf ? "Handle" : undefined}
      right={
        isSelf && (managedTeams.length > 0 || canCreate) ? (
          <Link to={teamFilter ? `/upload?team=${teamFilter}` : "/upload"}>+ Upload</Link>
        ) : undefined
      }
    >
      <div className="p" style={{ paddingTop: 20, paddingBottom: 40 }} data-testid="user-page">
        <ErrorLine error={q.error} />
        {!data ? (
          q.loading ? <p className="sub">Loading @{handle}…</p> : null
        ) : (
          <>
            <div className="up-bar">
              <span className="avatar lg blue">{initialsOf(data.user.display_name, data.user.handle)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row" style={{ justifyContent: "flex-start", gap: 8, flexWrap: "wrap" }}>
                  <h1 style={{ fontSize: 22 }}>{data.user.display_name}</h1>
                  <span className="sub">@{data.user.handle}</span>
                </div>
                <div className="tiny mt4">
                  {data.teams.length === 0
                    ? "No teams yet"
                    : `${data.teams.length} ${data.teams.length === 1 ? "team" : "teams"} · ${
                        data.teams.some((t) => t.role === "manager") ? "Manager" : "Member"
                      }`}
                </div>
              </div>
              <div className="row" style={{ gap: 8 }}>
                {isSelf && (
                  <button
                    type="button"
                    className="btn ghost sm"
                    onClick={() => setEditHandle(true)}
                    data-testid="user-edit-handle"
                  >
                    Edit @handle
                  </button>
                )}
                {isSelf && (
                  <Link to="/teams" className="btn ghost sm d-only">
                    Teams
                  </Link>
                )}
                {isSelf && (managedTeams.length > 0 || canCreate) && (
                  <Link to={teamFilter ? `/upload?team=${teamFilter}` : "/upload"} className="btn dark sm d-only">
                    Upload match
                  </Link>
                )}
              </div>
            </div>

            {data.teams.length > 0 && (
              <div className="fpill-bar mt16" data-testid="user-team-pills">
                <button
                  type="button"
                  className={`fpill ${!teamFilter ? "on" : ""}`}
                  onClick={() => setTeam("")}
                  data-testid="team-pill-all"
                >
                  All teams
                </button>
                {data.teams.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`fpill ${teamFilter === t.id ? "on" : ""}`}
                    onClick={() => setTeam(t.id)}
                    data-testid={`team-pill-${t.id}`}
                  >
                    {t.name}
                    <span className="role">{t.role === "manager" ? "Mgr" : "Member"}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="mt24" data-testid="user-clips-section">
              <div className="row">
                <h2>{isSelf ? "Your clips" : `@${data.user.handle}'s clips`}</h2>
                {data.clips && data.clips.matches.length > 0 && (
                  <span className="tiny">
                    {data.clips.matches.reduce((s, m) => s + m.clip_count, 0)} clips across {data.clips.matches.length}{" "}
                    {data.clips.matches.length === 1 ? "match" : "matches"}
                  </span>
                )}
              </div>

              {!data.clips || data.clips.matches.length === 0 ? (
                <div className="notice mt12" data-testid="user-clips-empty">
                  <div className="med">No clips assigned to @{data.user.handle} yet.</div>
                  <p className="tiny mt4">
                    When a team manager assigns a batting or bowling group to @{data.user.handle}, the clips appear here for free playback and download.
                  </p>
                </div>
              ) : (
                <div className="col mt16" style={{ gap: 20 }} data-testid="user-clip-matches">
                  {data.clips.matches.map((m) => {
                    const roleText = m.roles.map((r) => (r === "batsman" ? "Batting" : "Bowling")).join(" & ");
                    return (
                      <div key={m.video.id} data-testid={`user-match-${m.video.id}`}>
                        <div
                          style={{ cursor: "pointer" }}
                          onClick={() => nav(`/u/${data.user.handle}/clips?video=${m.video.id}`)}
                        >
                          <VidFrame
                            height={168}
                            play
                            poster={m.thumbnail_url}
                            tag={`${m.clip_count} ${m.clip_count === 1 ? "clip" : "clips"} · ${roleText || "Assigned"}`}
                            tr={m.video.team?.name ?? undefined}
                          />
                          <div className="row mt8">
                            <span className="med">{m.video.title}</span>
                            <span className="tiny">{fmtDate(m.video.created_at)}</span>
                          </div>
                        </div>
                        <div className="row mt8" style={{ justifyContent: "flex-start", gap: 8 }}>
                          <button
                            type="button"
                            className="btn dark sm"
                            onClick={() => nav(`/u/${data.user.handle}/clips?video=${m.video.id}`)}
                            data-testid={`play-user-clips-${m.video.id}`}
                          >
                            Play {m.clip_count} {m.clip_count === 1 ? "clip" : "clips"}
                          </button>
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => nav(`/u/${data.user.handle}/clips?video=${m.video.id}`)}
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {data.managed_videos && data.managed_videos.length > 0 && (
              <div className="mt32" data-testid="user-managed-videos">
                <div className="row">
                  <h2>Full match videos</h2>
                  <span className="tiny">Managed by you · {data.managed_videos.length}</span>
                </div>
                <div className="col mt16" style={{ gap: 20 }}>
                  {data.managed_videos.map((v: Video & { team_name: string | null }) => {
                    const s = videoStatus(v, plan);
                    const ready = v.state === "ready";
                    return (
                      <Link key={v.id} to={`/videos/${v.id}`} style={{ display: "block" }} data-testid={`managed-video-${v.id}`}>
                        <VidFrame
                          height={156}
                          play={ready}
                          poster={v.thumbnail_url}
                          tag={ready && v.clip_count != null ? `${v.clip_count} clips` : undefined}
                          tr={v.team_name ?? "Personal"}
                        />
                        <div className="row mt8">
                          <span className="med">{v.title}</span>
                          <span className="tiny">{fmtDuration(v.duration_ms) || fmtDate(v.created_at)}</span>
                        </div>
                        <div className="tiny mt4">
                          <Dot kind={s.dot} />
                          {s.words}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {isSelf && canCreate && data.teams.length === 0 && (
              <div className="notice mt24">
                <div className="row">
                  <div>
                    <div className="med">Manage a team?</div>
                    <div className="tiny mt4">Create a team to upload matches and assign clips to your players' @handles.</div>
                  </div>
                  <Link to="/teams" className="btn ghost sm">Create a team</Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {editHandle && (
        <HandleSheet
          initialHandle={data?.user.handle ?? handle}
          onClose={() => setEditHandle(false)}
          onSaved={(newH) => {
            say(`Handle updated to @${newH}`);
            if (newH !== handle) nav(`/u/${newH}${teamFilter ? `?team=${teamFilter}` : ""}`, { replace: true });
            else void q.reload();
          }}
        />
      )}
      {toast}
    </Shell>
  );
}
